import React from 'react';
import { Box, Button, Grid2 as Grid, Paper, Typography } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { filterPaper, filterHeader } from '../../styles/pageStyles';

/**
 * PageFilters — Componente template padrão de bloco de filtros.
 *
 * Encapsula a estrutura visual padrão de um bloco de filtros (Paper + cabeçalho
 * "Filtros" + ícone + botão "Limpar" + grid de controles). Garante consistência
 * estrutural — toda página DEVE usar este componente para exibir filtros.
 *
 * Regras estruturais (não negociáveis, aplicadas internamente):
 *  - Container sempre Paper outlined com `filterPaper` sx
 *  - Cabeçalho sempre com FilterListIcon + label "Filtros" + botão "Limpar"
 *  - Conteúdo sempre dentro de Grid container spacing 1.5
 *  - Botão limpar sempre alinhado à direita, outlined, size small
 *
 * Os children devem ser `<Grid>` items (compatível com MUI Grid v1 ou Grid2):
 *
 *   <PageFilters onClear={...} clearDisabled={!hasFilters}>
 *     <Grid item xs={12} md={4}><TextField ... /></Grid>
 *     <Grid item xs={12} md={4}><TextField ... /></Grid>
 *   </PageFilters>
 *
 * @param {function} onClear        Callback do botão "Limpar".
 * @param {boolean}  clearDisabled  Quando true, desabilita o botão "Limpar".
 * @param {string}   clearLabel     Label do botão (default "Limpar").
 * @param {string}   title          Label do cabeçalho (default "Filtros").
 * @param {React.ReactNode} actions Conteúdo extra à direita do título (antes do botão Limpar).
 * @param {React.ReactNode} children Items do Grid de filtros.
 */
export default function PageFilters({
  onClear,
  clearDisabled = false,
  clearLabel = 'Limpar',
  title = 'Filtros',
  actions,
  children,
}) {
  return (
    <Paper variant="outlined" sx={filterPaper}>
      <Box sx={filterHeader}>
        <FilterListIcon fontSize="small" color="action" />
        <Typography
          variant="subtitle2"
          color="text.secondary"
          fontWeight="medium"
        >
          {title}
        </Typography>
        {actions}
        {onClear && (
          <Button
            size="small"
            variant="outlined"
            onClick={onClear}
            disabled={clearDisabled}
            sx={{ ml: 'auto' }}
          >
            {clearLabel}
          </Button>
        )}
      </Box>
      <Grid container spacing={1.5}>
        {children}
      </Grid>
    </Paper>
  );
}
